export type LayoutVariant = 'expanded-dense' | 'collapsed-dense' | 'auto';

export const THEME_STORAGE_KEY = 'theme';
export const PRIMARY_COLOR_STORAGE_KEY = 'app-primary-color';
export const LAYOUT_VARIANT_STORAGE_KEY = 'app-layout-variant';
export const WORKSPACE_STORAGE_KEY = 'app-workspace';

export const layoutVariants: Array<{
  value: LayoutVariant;
  label: string;
  description: string;
}> = [
  {
    value: 'expanded-dense',
    label: 'Expanded dense',
    description: 'Keeps the full navigation tree visible for desktop workflows.',
  },
  {
    value: 'collapsed-dense',
    label: 'Collapsed dense',
    description: 'Uses the compact icon rail with hover discovery for nested navigation.',
  },
  {
    value: 'auto',
    label: 'Auto',
    description: 'Follows the responsive shell behavior for the current device width.',
  },
];

export const workspaceOptions = [
  {
    id: 'ois-rollout',
    label: 'OIS Rollout',
    description: 'Primary rollout workspace',
  },
  {
    id: 'release-ops',
    label: 'Release Ops',
    description: 'Operations and delivery controls',
  },
  {
    id: 'platform-admin',
    label: 'Platform Admin',
    description: 'Administration and configuration workspace',
  },
];

const DEFAULT_PRIMARY_OKLCH = '0.602 0.23 281.4';
const DEFAULT_PRIMARY_FOREGROUND_OKLCH = '0.98 0.002 45';

export const primaryColorPresets = [
  { label: 'Electric Violet', value: '#7161FF' },
  { label: 'Signal Orange', value: '#FF5733' },
  { label: 'Reference Orange', value: '#EA580C' },
  { label: 'Copper Clay', value: '#C46A39' },
  { label: 'Warm Amber', value: '#D97706' },
  { label: 'Signal Blue', value: '#0284C7' },
  { label: 'Teal', value: '#0F766E' },
  { label: 'Emerald', value: '#059669' },
  { label: 'Crimson', value: '#DC2626' },
  { label: 'Graphite', value: '#475569' },
];

const DEFAULT_PRIMARY_COLOR = primaryColorPresets[0].value;
const LEGACY_DEFAULT_PRIMARY_COLORS = new Set([
  '#3CA0E4',
  '#1A1A2F',
  '#BF81FF',
  '#C084FC',
  '#FF5733',
  '#EA580C',
]);
const DEFAULT_LAYOUT_VARIANT: LayoutVariant = 'expanded-dense';
const DEFAULT_WORKSPACE = workspaceOptions[0].id;

const LIGHT_FOREGROUND = '0.98 0.002 45';
const DARK_FOREGROUND = '0.2 0.005 45';

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const normalizeHex = (value?: string | null) => {
  if (!value) return '';

  const trimmed = value.trim().toUpperCase();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;

  if (/^#[0-9A-F]{3}$/.test(withHash)) {
    return `#${withHash[1]}${withHash[1]}${withHash[2]}${withHash[2]}${withHash[3]}${withHash[3]}`;
  }

  if (/^#[0-9A-F]{6}$/.test(withHash)) {
    return withHash;
  }

  return '';
};

export const isValidHexColor = (value?: string | null) => Boolean(normalizeHex(value));

const hexToRgb = (hex: string) => {
  const normalized = normalizeHex(hex) || DEFAULT_PRIMARY_COLOR;

  return {
    r: Number.parseInt(normalized.slice(1, 3), 16) / 255,
    g: Number.parseInt(normalized.slice(3, 5), 16) / 255,
    b: Number.parseInt(normalized.slice(5, 7), 16) / 255,
  };
};

const srgbToLinear = (value: number) =>
  value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);

const linearToRelativeLuminance = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  const rl = srgbToLinear(r);
  const gl = srgbToLinear(g);
  const bl = srgbToLinear(b);

  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
};

const toOklch = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  const rl = srgbToLinear(r);
  const gl = srgbToLinear(g);
  const bl = srgbToLinear(b);

  const x = 0.4122214708 * rl + 0.5363325363 * gl + 0.0514459929 * bl;
  const y = 0.2119034982 * rl + 0.6806995451 * gl + 0.1073969566 * bl;
  const z = 0.0883024619 * rl + 0.2817188376 * gl + 0.6299787005 * bl;

  const l = Math.cbrt(0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z);
  const m = Math.cbrt(0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z);
  const s = Math.cbrt(0.0482003018 * x + 0.2643662691 * y + 0.633851707 * z);

  const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bValue = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const chroma = Math.sqrt(a * a + bValue * bValue);
  const hue = ((Math.atan2(bValue, a) * 180) / Math.PI + 360) % 360;

  return {
    l: clamp(lightness, 0, 1),
    c: clamp(chroma, 0, 0.37),
    h: hue,
  };
};

const formatOklch = ({ l, c, h }: { l: number; c: number; h: number }) =>
  `${l.toFixed(3)} ${c.toFixed(3)} ${h.toFixed(1)}`;

const getForegroundToken = (hex: string) =>
  linearToRelativeLuminance(hex) > 0.42 ? DARK_FOREGROUND : LIGHT_FOREGROUND;

export const getStoredPrimaryColor = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_PRIMARY_COLOR;
  }

  const stored = normalizeHex(window.localStorage.getItem(PRIMARY_COLOR_STORAGE_KEY));

  if (!stored || LEGACY_DEFAULT_PRIMARY_COLORS.has(stored)) {
    return DEFAULT_PRIMARY_COLOR;
  }

  return stored;
};

export const setStoredPrimaryColor = (hex: string) => {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(
    PRIMARY_COLOR_STORAGE_KEY,
    normalizeHex(hex) || DEFAULT_PRIMARY_COLOR
  );
};

export const applyPrimaryColor = (hex: string) => {
  if (typeof document === 'undefined') return;

  const normalized = normalizeHex(hex) || DEFAULT_PRIMARY_COLOR;
  const primaryToken =
    normalized === DEFAULT_PRIMARY_COLOR ? DEFAULT_PRIMARY_OKLCH : formatOklch(toOklch(normalized));
  const foreground =
    normalized === DEFAULT_PRIMARY_COLOR
      ? DEFAULT_PRIMARY_FOREGROUND_OKLCH
      : getForegroundToken(normalized);

  const root = document.documentElement;
  root.style.setProperty('--primary-light', primaryToken);
  root.style.setProperty('--primary-dark', primaryToken);
  root.style.setProperty('--ring-light', primaryToken);
  root.style.setProperty('--ring-dark', primaryToken);
  root.style.setProperty('--icon-bar-active', primaryToken);
  root.style.setProperty('--primary-foreground-light', foreground);
  root.style.setProperty('--primary-foreground-dark', foreground);
};

export const getStoredLayoutVariant = (): LayoutVariant => {
  if (typeof window === 'undefined') {
    return DEFAULT_LAYOUT_VARIANT;
  }

  const value = window.localStorage.getItem(LAYOUT_VARIANT_STORAGE_KEY);
  if (value === 'expanded-dense' || value === 'collapsed-dense' || value === 'auto') {
    return value;
  }

  return DEFAULT_LAYOUT_VARIANT;
};

export const setStoredLayoutVariant = (value: LayoutVariant) => {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(LAYOUT_VARIANT_STORAGE_KEY, value);
};

export const getStoredWorkspace = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_WORKSPACE;
  }

  return window.localStorage.getItem(WORKSPACE_STORAGE_KEY) || DEFAULT_WORKSPACE;
};

export const setStoredWorkspace = (value: string) => {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(WORKSPACE_STORAGE_KEY, value);
};

export const getPrimaryColorLabel = (hex: string) => {
  const normalized = normalizeHex(hex);
  const preset = primaryColorPresets.find((option) => option.value === normalized);
  return preset?.label || 'Custom primary';
};

export const initializeShellPreferences = () => {
  const primaryColor = getStoredPrimaryColor();
  applyPrimaryColor(primaryColor);
  setStoredPrimaryColor(primaryColor);
};
